import type { AstroLocale } from '@/schemas/components/shared/i18n';
import freeEmailDomains from 'free-email-domains/domains.json';
import { useEffect, useRef } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { withForm } from '../hooks/form';
import { autoPrependDialCode } from '../lib/autoPrependDialCode';
import { COUNTRIES, PROVINCES, STATES } from '../select-values';
import { ERROR_MESSAGES, INPUT_LABELS } from '../translations';
import { gatedContentFormOptions } from './formOptions';

export const GatedContentFormFields = withForm({
  ...gatedContentFormOptions,
  props: {
    language: 'en' as AstroLocale,
    sortedCountries: COUNTRIES,
  },
  render: ({ form, language, sortedCountries }) => (
    <>
      {language === 'zh' ?
        <div className="flex flex-col gap-x-8 @sm:flex-row">
          <form.AppField
            name="Name"
            validators={{
              onChange: ({ value }) => {
                if (!value) return ERROR_MESSAGES.empty[language];
              },
            }}
          >
            {(field) => <field.TextInput label={INPUT_LABELS.name[language]} />}
          </form.AppField>
        </div>
      : <div className="flex flex-col gap-x-8 @sm:flex-row">
          <form.AppField
            name="first_name"
            validators={{
              onChange: ({ value }) => {
                if (!value) return ERROR_MESSAGES.empty[language];
              },
            }}
          >
            {(field) => (
              <field.TextInput label={INPUT_LABELS.firstName[language]} />
            )}
          </form.AppField>
          <form.AppField
            name="last_name"
            validators={{
              onChange: ({ value }) => {
                if (!value) return ERROR_MESSAGES.empty[language];
              },
            }}
          >
            {(field) => (
              <field.TextInput label={INPUT_LABELS.lastName[language]} />
            )}
          </form.AppField>
        </div>
      }
      <div className="flex flex-col gap-x-8 @sm:flex-row">
        <form.AppField
          name="email"
          validators={{
            onBlur: ({ value }) => {
              if (!value) return ERROR_MESSAGES.empty[language];
              const [username, domain] = value.split('@');

              if (!domain || username.length < 1) {
                return ERROR_MESSAGES.emailValid[language];
              }

              if (freeEmailDomains.includes(domain)) {
                return ERROR_MESSAGES.emailDomain[language];
              }
            },
          }}
        >
          {(field) => <field.TextInput label={INPUT_LABELS.email[language]} />}
        </form.AppField>
        <form.AppField
          name="country"
          validators={{
            onChange: ({ value }) => {
              if (!value) return ERROR_MESSAGES.empty[language];
            },
          }}
        >
          {(field) => (
            <field.SelectDropdown
              items={sortedCountries}
              placeholder={INPUT_LABELS.country[language]}
              language={language}
            />
          )}
        </form.AppField>
      </div>
      <div className="flex flex-col gap-x-8 @sm:flex-row">
        <form.AppField
          name="company"
          validators={{
            onChange: ({ value }) => {
              if (!value) return ERROR_MESSAGES.empty[language];
            },
          }}
        >
          {(field) => (
            <field.TextInput label={INPUT_LABELS.companyName[language]} />
          )}
        </form.AppField>
        <form.Subscribe selector={({ values }) => values.country}>
          {(country) => {
            const prevCountryRef = useRef<string | null>(null);

            useEffect(() => {
              const prevCountry = prevCountryRef.current;
              prevCountryRef.current = country;
              const phone = form.getFieldValue('phone') || '';

              const newPhone = autoPrependDialCode({
                phone,
                country,
                prevCountry,
                sortedCountries,
                language,
              });

              if (newPhone !== phone) {
                form.setFieldValue('phone', newPhone);
                form.validateField('phone', 'change');
              }
            }, [country]);

            return null;
          }}
        </form.Subscribe>
        <form.AppField
          name="phone"
          validators={{
            onBlur: ({ value }) => {
              if (!value) return ERROR_MESSAGES.empty[language];
              if (!isValidPhoneNumber(value))
                return ERROR_MESSAGES.phone[language];
            },
          }}
        >
          {(field) => (
            <field.PhoneNumberInput
              label={INPUT_LABELS.phoneNumber[language]}
            />
          )}
        </form.AppField>
      </div>
      <form.Subscribe selector={({ values }) => values.country}>
        {(country) => {
          if (country === 'United States' || country === 'Canada') {
            const isUnitedStates = country === 'United States';
            const placeholder = isUnitedStates ? 'state' : 'province';
            const items = isUnitedStates ? STATES : PROVINCES;

            return (
              <div className="flex flex-col @sm:flex-row @sm:gap-8">
                <form.AppField
                  name="state"
                  validators={{
                    onChange: ({ value }) => {
                      if (!value) return ERROR_MESSAGES.empty[language];
                    },
                  }}
                >
                  {(field) => (
                    <field.SelectDropdown
                      items={items}
                      placeholder={INPUT_LABELS[placeholder][language]}
                      language={language}
                    />
                  )}
                </form.AppField>
              </div>
            );
          }
        }}
      </form.Subscribe>
      <div className="mb-9">
        <form.AppField name="Opt_In">
          {(field) => (
            <field.ConsentCheckbox
              copy={INPUT_LABELS.emailMarketing[language]}
            />
          )}
        </form.AppField>
      </div>
    </>
  ),
});
